// useUpcomingMissions: Data hook for the agenda list view in TeamView
// Fetches all missions for the selected period (week or month) for a given site
// Handles admin/director (no user_sites check) vs manager roles
// Enriches tasks with recurrence frequency/interval from recurrence_rules table
// CREATED: Part of the agenda list view for TeamView
// FIXED: Non-AbortError exceptions now logged to console for debugging
// FIXED: recurrence_rules query error is now checked and logged instead of silently ignored
// FIX: Added requestId guard to prevent stale async responses from overwriting newer state
// FIX: AbortController.signal now passed to the inline Supabase recurrence_rules query
// UPDATED: AbortSignal is now forwarded into getAllTeamTasksForDateRange and getTeamTasksForDateRange
//          so underlying Supabase network requests are cancelled when the controller is aborted
// FIX: Abort and requestId increment now happen before guard so invalid-params branch cancels
//      in-flight requests and resets all loading/refreshing/error state
// UPDATED: Early-return guard is now role-aware: userId is only required for non-admin/director
//          roles so getAllTeamTasksForDateRange (which needs no userId) can run for admins/directors
// UPDATED: Added RecurrenceRuleRow interface to replace any in recurrence_rules forEach

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { getTeamTasksForDateRange, getAllTeamTasksForDateRange } from '../lib/execution';
import { formatDate } from '../lib/dateUtils';
import type { EnrichedMission } from '../components/manager/agenda/types';

interface RecurrenceRuleRow {
  template_id: string;
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number | null;
}

interface UseUpcomingMissionsParams {
  userId: string | null;
  userRole: string | null;
  siteId: string | null;
  periodStart: Date | null;
  periodEnd: Date | null;
}

interface UseUpcomingMissionsResult {
  tasks: EnrichedMission[];
  loading: boolean;
  error: string | null;
  isRefreshing: boolean;
  refresh: () => void;
}

export function useUpcomingMissions({
  userId,
  userRole,
  siteId,
  periodStart,
  periodEnd,
}: UseUpcomingMissionsParams): UseUpcomingMissionsResult {
  const [tasks, setTasks] = useState<EnrichedMission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const fetchTasks = useCallback(async (isRefresh = false) => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const currentRequestId = ++requestIdRef.current;

    const isAdminOrDirector = userRole === 'admin' || userRole === 'director';

    if (!userRole || !siteId || !periodStart || !periodEnd || (!isAdminOrDirector && !userId)) {
      setTasks([]);
      setLoading(false);
      setIsRefreshing(false);
      setError(null);
      return;
    }

    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    const startStr = formatDate(periodStart);
    const endStr = formatDate(periodEnd);

    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const rawTasks = isAdminOrDirector
        ? await getAllTeamTasksForDateRange(siteId, startStr, endStr, signal)
        : await getTeamTasksForDateRange(userId, startStr, endStr, siteId, signal);

      if (currentRequestId !== requestIdRef.current) return;

      const recurringTemplateIds = [
        ...new Set(
          rawTasks
            .filter(t => t.is_recurring && t.template_id)
            .map(t => t.template_id)
        ),
      ];

      const rulesByTemplateId = new Map<string, { frequency: 'daily' | 'weekly' | 'monthly' | 'yearly'; interval: number }>();

      if (recurringTemplateIds.length > 0) {
        const { data: rules, error: rulesError } = await supabase
          .from('recurrence_rules')
          .select('template_id, frequency, interval')
          .in('template_id', recurringTemplateIds)
          .abortSignal(signal);

        if (rulesError) {
          console.error('[useUpcomingMissions] recurrence_rules fetch failed:', rulesError);
        }

        if (currentRequestId !== requestIdRef.current) return;

        (rules as RecurrenceRuleRow[] || []).forEach((r) => {
          rulesByTemplateId.set(r.template_id, {
            frequency: r.frequency,
            interval: r.interval ?? 1,
          });
        });
      }

      const enriched: EnrichedMission[] = rawTasks.map(task => {
        const rule = task.is_recurring ? rulesByTemplateId.get(task.template_id) : undefined;
        return {
          ...task,
          recurrenceFrequency: rule?.frequency ?? null,
          recurrenceInterval: rule?.interval ?? 1,
        };
      });

      if (currentRequestId !== requestIdRef.current) return;

      setTasks(enriched);
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        console.error('[useUpcomingMissions] fetch failed:', err);
        if (currentRequestId === requestIdRef.current) {
          setError('Erreur lors du chargement des missions');
        }
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
        setIsRefreshing(false);
      }
    }
  }, [userId, userRole, siteId, periodStart, periodEnd]);

  useEffect(() => {
    fetchTasks(false);
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchTasks]);

  const refresh = useCallback(() => fetchTasks(true), [fetchTasks]);

  return { tasks, loading, error, isRefreshing, refresh };
}
